import { Component } from '@theme/component';

/**
 * Before & After results carousel (sections/before-after-results.liquid).
 *
 * The carousel itself is assets/custom-slider.js. This element only adds:
 *
 * - the compare handle: each card's invisible range input moves `--pos` on its
 *   photo frame, which the CSS uses for the clip, divider and handle;
 * - drag isolation: custom-slider.js swipes on mousedown/touchstart anywhere in
 *   its track, so a drag on the photo would also change slide. Stopping those
 *   events at the input keeps the drag on the comparison;
 * - theme-editor support: custom-slider.js only initialises on
 *   DOMContentLoaded, so a section the editor adds or re-renders later is
 *   initialised here instead.
 *
 * @extends {Component<{ ranges?: HTMLInputElement[] }>}
 */
class BeforeAfterResults extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const stop = (/** @type {Event} */ event) => event.stopPropagation();

    for (const range of this.refs.ranges ?? []) {
      range.addEventListener('mousedown', stop, { signal });
      range.addEventListener('touchstart', stop, { signal, passive: true });
    }

    // After `load`, custom-slider.js's DOMContentLoaded pass is long over, so
    // this markup arrived from the theme editor and nothing else will set it up.
    // `AdvancedSlider` is the top-level class of that classic script.
    if (document.readyState === 'complete' && typeof AdvancedSlider === 'function') {
      const slider = this.querySelector('.custom-slider');
      // eslint-disable-next-line no-undef
      if (slider) new AdvancedSlider(slider);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Moves the divider to the range value.
   *
   * @param {Event} event - The input event, delegated from `on:input`.
   */
  compare(event) {
    const range = event.target;

    if (!(range instanceof HTMLInputElement)) return;

    range.parentElement?.style.setProperty('--pos', `${range.value}%`);
  }
}

if (!customElements.get('before-after-results')) {
  customElements.define('before-after-results', BeforeAfterResults);
}
